import { Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useProjectAttention, useProjectOpenItems } from "@/hooks/use-project-attention";
import { useProjectPulse } from "@/hooks/use-project-pulse";
import type { Project } from "@/hooks/use-projects";
import { useSession } from "@/lib/auth-client";
import { ActionQueueCard } from "./ActionQueueCard";
import { ActivityCard } from "./ActivityCard";
import { OpenItemsStrip } from "./OpenItemsStrip";
import { PulseStrip } from "./PulseStrip";
import { ScheduleCard } from "./ScheduleCard";
import { TeamCard } from "./TeamCard";

interface OverviewTabProps {
  project: Project;
  onEditClick: () => void;
}

export function OverviewTab({ project, onEditClick }: OverviewTabProps) {
  const pid = project.id;
  const { data: session } = useSession();
  const isContractor = session?.user?.role === "contractor";

  const pulseQuery = useProjectPulse(pid);
  const attentionQuery = useProjectAttention(pid);
  const openItemsQuery = useProjectOpenItems(pid);

  const scheduleProgress = project.percentComplete ? Number(project.percentComplete) : 0;

  return (
    <div className="space-y-6">
      {!isContractor && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={onEditClick} className="rounded-lg">
            <Pencil className="mr-2 h-4 w-4" />
            Edit Project
          </Button>
        </div>
      )}

      <PulseStrip
        pulse={pulseQuery.data}
        scheduleProgress={scheduleProgress}
        isLoading={pulseQuery.isLoading}
      />

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8 flex flex-col gap-6">
          <ActionQueueCard items={attentionQuery.data} isLoading={attentionQuery.isLoading} />
          <OpenItemsStrip data={openItemsQuery.data} isLoading={openItemsQuery.isLoading} />
        </div>
        <div className="col-span-12 lg:col-span-4 flex flex-col gap-6">
          <TeamCard projectId={pid} />
          <ScheduleCard
            projectId={pid}
            startDate={project.startDate ?? null}
            endDate={project.endDate ?? null}
            scheduleProgress={scheduleProgress}
          />
          <ActivityCard projectId={pid} />
        </div>
      </div>
    </div>
  );
}
